import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import { fetchWithRetry } from 'utils/fetchWithRetry';
import { FetchOptions } from 'types/fetchWithRetry';
import { HttpRequestMethodTypes, JSON_CONTENT_TYPE_HEADER } from 'types'; 
import Button, { PRIMARY_THEME, SECONDARY_THEME } from 'components/control/button';
import PillBadge from 'components/atomic/organisms/pill-badge';
import { InfoIcon } from 'components/atomic/atoms/icons';
import CloseIcon from 'components/icon/close-icon';
import useOutsideClickHandler from 'hooks/use-outside-click-handler';
import styles from './style.module.scss';
import { PropertyTypeFilter } from 'contexts/preferences/listing-params';
import type { Filter, Status } from 'contexts/preferences/listing-params';
import { ListingFilterBarType } from 'components/listing-filter-bar/listing_filter_bar';
import type { PartialDeep } from 'type-fest';
import { SearchParameters } from 'data/conversational-search';
import { fetchAreaPolygons } from 'data/search/map/fetcher';
import { parseWktPoint } from 'components/search-es/geo_helper';
import { provinceOrStateSlugFromCode, provinceOrStateNameFromCode, provinceOrStateCodeFromString, countryCodeFromProvinceOrState, type ProvinceOrStateCode } from 'utils/province_or_state';
import { getActiveFilters } from 'components/listing-filter-bar/listing_filter_bar';
import { useUserContext } from 'contexts/user';
import { useTenantId } from 'hooks/use-tenant-id';
import { createConversation, deleteAllConversations, deleteConversation, listConversations, getConversation, updateConversation } from 'utils/smartchat/conversations-api';
import { createMessage, listMessages, Role } from 'utils/smartchat/messages-api';
import { buildClassName } from 'utils/build-class-name';
import { defaultCAMapCenter, defaultUSMapCenter } from 'constants/locations';
import { sub250Chars } from 'utils/stringSanitization';
import PaperAirplaneIcon from 'components/icon/paper-airplane-icon';
import { containsCompetitorName } from 'utils/stringSanitization';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { CompetitorError } from 'pages/api/conversational-search';
import { CountryCodeList } from 'types/countries';
import { CLEAR_CHAT_MODAL, IModalContext, ModalContext } from 'contexts/modal';
import { ClearChatOptions } from 'components/modal-dialog/clear-chat';
import { mergeSearchParamsWithFilter } from 'utils/conversational-search/merge-search-params';
import { parseNaturalLanguageQueryIntoFilters } from 'utils/parse-natural-language-query-into-filters/parse-natural-language-query-into-filters';
import { mergeFiltersBasedOnIntent } from 'data/conversational-search/nlp-processor';
import isEqual from 'lodash/isEqual';
import MessageVoteButtons, { Vote, VoteType } from 'components/chat-widget/MessageVoteButtons';
import { trackSmartchatSessionEnd, trackSmartchatNewUserMessage, trackSmartchatError, trackSmartchatFilterApplied } from 'utils/smartchat-tracking';

/**
 * Determine vote from response message's meta tags.
 */
function getVoteFromMetadata(metadata?: Record<string, string>): VoteType {
  if (!metadata?.tags) {
    return null;
  }

  let tags: string[];
  try {
    tags = JSON.parse(metadata.tags) as string[];
  } catch {
    console.error('Metadata tags is not valid JSON', metadata.tags);
    return null;
  }

  const hasUpvote = tags.includes(Vote.UPVOTE);
  const hasDownvote = tags.includes(Vote.DOWNVOTE);

  if (hasUpvote && hasDownvote) {
    // Should not get here: Both votes present, treat as no vote
    return null;
  }
  else if (hasUpvote) {
    return Vote.UPVOTE;
  }
  else if (hasDownvote) {
    return Vote.DOWNVOTE;
  }
  else {
    return null;
  }
}

const ZOOM_STATE_LEVEL = 6;
const ZOOM_DEFAULT = 12;

/** Keys in SearchParameters that relate to location/geography */
const LOCATION_PARAM_KEYS: (keyof SearchParameters)[] = ['city', 'province', 'country', 'neighbourhood', 'boundaryTopLeft', 'boundaryBottomRight'];

/**
 * Formats suggestions list for display in chat
 * Joins an array of suggestions with commas, but uses spaces when the previous suggestion ends with punctuation. 
 **/
function formatSuggestions(suggestions: string[]): string {
  return suggestions.map((suggestion, index) => {
    const trimmed = suggestion.trim();
    if (index === 0) return trimmed;
    
    const prevEndsWithPunctuation = /[?!.,;:]$/.test(suggestions[index - 1]?.trim() || '');
    return prevEndsWithPunctuation ? ` ${trimmed}` : `, ${trimmed}`;
  }).join('');
}

interface ChatMessageUI {
  id: string;
  role: 'assistant' | 'user';
  content: string;
  timestamp: Date;
  savedMessageId?: string;
  conversationId?: string;
  metadata?: Record<string, string>;
  isWelcomeMessage?: boolean;
}

interface ChatBotProps {
  onSearchParamsChange?: (params: SearchParameters) => void;
  onFiltersChanged?: (changedFilters: PartialDeep<Filter>, changedFilter?: string) => void;
  onMapLocationChange?: (location: { lat: number; lng: number; zoom?: number }) => void;
  className?: string;
  filters: ListingFilterBarType;
  totalListingsCount?: number;
  /** When true, location-related params (city, province, country, etc.) are stripped
   *  from AI responses and a message directs the user to use the search bar instead. */
  disableLocationSearch?: boolean;
  /** When true, the built-in header (title, BETA badge, Clear, info icon) is hidden.
   *  Use this when the parent component provides its own header. */
  hideHeader?: boolean;
  /** Content to render between the messages area and the input field. */
  renderBeforeInput?: React.ReactNode;
  /** Ref that will be populated with the clear-chat handler so the parent can trigger it. */
  clearRef?: React.MutableRefObject<(() => void) | null>;
  /** Ref that will be populated with a send-message handler so the parent can trigger sends programmatically. */
  sendMessageRef?: React.MutableRefObject<((text: string) => void) | null>;
  /** When true, only show the listing count response. */
  onlyShowListingCountResponse?: boolean;
  /** Fired whenever the chat's loading (waiting-for-AI) state flips.
   *  Lets the parent synchronise UI (e.g. hide suggestion chips) while a
   *  response is in flight. */
  onLoadingChange?: (isLoading: boolean) => void;
  /** Example city used in the welcome message's sample query (e.g. "3 bedroom
   *  houses in <exampleCity> under $800k"). Defaults to "Miami" when omitted
   *  to preserve behaviour for US-focused tenants. */
  exampleCity?: string;
}

const ChatBot = ({ 
  onSearchParamsChange,
  onFiltersChanged,
  onMapLocationChange,
  className,
  filters,
  totalListingsCount,
  disableLocationSearch,
  hideHeader,
  renderBeforeInput,
  clearRef,
  sendMessageRef,
  onlyShowListingCountResponse,
  onLoadingChange,
  exampleCity = 'Miami',
}: ChatBotProps) => {
  const { t } = useTranslation(Namespaces.COMMON);
  const [messages, setMessages] = useState<ChatMessageUI[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // True between the moment a filter-applying response returns and the moment the
  // follow-up listing-count message is actually appended to the chat. This bridges
  // the gap after `isLoading` flips to false so consumers (e.g. the hybrid page
  // chat panel) can keep suggestion chips hidden until the final message renders.
  const [isAwaitingFinalMessage, setIsAwaitingFinalMessage] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [showDisclaimerTooltip, setShowDisclaimerTooltip] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { openModal } = useContext(ModalContext) as IModalContext;
  
  const messageIdCounter = useRef(0);
  const hasInitialized = useRef(false);
  const lastSearchParamsRef = useRef<SearchParameters | null>(null);
  const previousSearchParamsRef = useRef<SearchParameters>({});
  const lastAppliedFilterRef = useRef<string | null>(null);
  const awaitingFinalMessageTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Context hooks
  const { user, isAuthenticated, aiSearchParams, setAiSearchParams, userLocation } = useUserContext();
  const tenantId = useTenantId();
  const prevIsAuthenticated = useRef(isAuthenticated);
  const disclaimerText = t(LOCALE_COMMON_PATHS.SMART_CHAT_DISCLAIMER_TEXT, { defaultValue: 'This is an AI-powered real estate assistant. Information provided may be inaccurate. This tool is for informational purposes only and should not be considered financial, legal, or investment advice. We use your input to improve the service and do not sell your data.' });

  const generateMessageId = useCallback(() => {
    messageIdCounter.current += 1;
    return `msg-${Date.now()}-${messageIdCounter.current}`;
  }, []);

  // Get current location context for the chat
  const getCurrentLocationContext = useCallback(() => {
    // Priority order: 1) Current search area, 2) User's detected location, 3) No location
    
    // 1. Check current search area first
    if (filters.areaName && filters.areaName !== 'USA') {
      return filters.areaName;
    }
    if (filters.slug) {
      return filters.slug;
    }
    
    // 2. Use user's detected location
    if (userLocation?.name) {
      // Skip broad locations like "USA" or "United States"
      if (userLocation.name === 'USA' || userLocation.name === 'United States') {
        return null;
      }
      
      // Extract city name from location (e.g., "Toronto, ON" -> "Toronto")
      const cityName = userLocation.name.split(',')[0]?.trim();
      return cityName || userLocation.name;
    }
    
    return null;
  }, [filters.areaName, filters.slug, userLocation?.name]);

  const [accumulatedParams, setAccumulatedParams] = useState<SearchParameters>({});

  type MapApplyResult = {
    usedStateFallback: boolean;
    stateCode?: ProvinceOrStateCode;
    usedCountryFallback: boolean;
    cityNotFound?: string; // City name that was attempted but not found
  };

  const applySearchParams = useCallback(async (params: SearchParameters, _promptOnFallback?: boolean): Promise<MapApplyResult> => {
    // Applying search parameters to map
    
    if (!onFiltersChanged) {
      console.warn('onFiltersChanged not provided, cannot update filters');
      return { usedStateFallback: false, stateCode: undefined, usedCountryFallback: false };
    }

    const filterChanges: PartialDeep<Filter> & { city?: string; province?: string } = {};
    let usedStateFallback = false;
    let stateCodeForFallback: ProvinceOrStateCode | undefined;
    
    // Check for parameter removals (previously existed but now missing)
    if (accumulatedParams.bedrooms && !params.bedrooms) {
      filterChanges.bedrooms = '0+'; // Reset to default "Any Beds"
    }
    
    if (accumulatedParams.bathrooms && !params.bathrooms) {
      filterChanges.bathrooms = '0+'; // Reset to default "Any Baths"
    }
    
    if (accumulatedParams.parkingSpaces && !params.parkingSpaces) {
      filterChanges.parkingSpaces = '0+'; // Reset to default
    }

    if (params.city) {
      filterChanges.city = params.city;
    }
    
    if (params.province) {
      filterChanges.province = params.province;
    }
    
    if (params.bedrooms) {
      filterChanges.bedrooms = params.bedrooms;
    }
    
    if (params.bathrooms) {
      filterChanges.bathrooms = params.bathrooms;
    }
    
    // Check if price parameters were removed (existed before but not in new params)
    if (accumulatedParams.priceMin !== undefined && params.priceMin === undefined) {
      filterChanges.priceMin = undefined; // Explicitly remove
    }
    if (accumulatedParams.priceMax !== undefined && params.priceMax === undefined) {
      filterChanges.priceMax = undefined; // Explicitly remove
    }
    
    // Apply new price parameters if they exist, with validation
    let finalPriceMin = params.priceMin;
    let finalPriceMax = params.priceMax;
    
    // Validate: if both min and max exist, ensure min < max
    // If min > max, it's an invalid range - clear min (user likely said "under X")
    if (finalPriceMin !== undefined && finalPriceMax !== undefined && finalPriceMin > finalPriceMax) {
      finalPriceMin = undefined; // Clear invalid min
    }
    
    if (finalPriceMin !== undefined) {
      filterChanges.priceMin = finalPriceMin;
    }
    if (finalPriceMax !== undefined) {
      filterChanges.priceMax = finalPriceMax;
    }
    
    if (params.homeType) {
      // Ensure we send a complete PropertyTypeFilter object
      // If only some types are specified, set others to false
      const completeHomeType = {
        house: false,
        condo: false,
        townhouse: false,
        farm: false,
        land: false,
        commercial: false,
        ...params.homeType, // Override with specified types
      };
      filterChanges.homeType = completeHomeType as PropertyTypeFilter;
    }
    
    if (params.rental !== undefined) {
      filterChanges.rental = params.rental;
    }
    
    if (params.status) {
      // tslint:disable-next-line:no-any
      filterChanges.status = params.status as Status;
    }
    
    // Handle boolean filters - check if they were removed (existed before but not in new params)
    // When a filter is REMOVED via "remove pool filter", params.pool will be undefined
    // but we need to explicitly set it to false to clear the chip
    
    if (params.garage !== undefined) {
      filterChanges.garage = params.garage;
    } else if (accumulatedParams.garage === true) {
      // Filter was removed - explicitly set to false to clear the chip
      filterChanges.garage = false;
    }
    
    if (params.pool !== undefined) {
      filterChanges.pool = params.pool;
    } else if (accumulatedParams.pool === true) {
      // Filter was removed - explicitly set to false to clear the chip
      filterChanges.pool = false;
    }
    
    if (params.fireplace !== undefined) {
      filterChanges.fireplace = params.fireplace;
    } else if (accumulatedParams.fireplace === true) {
      // Filter was removed - explicitly set to false to clear the chip
      filterChanges.fireplace = false;
    }
    
    if (params.waterfront !== undefined) {
      filterChanges.waterfront = params.waterfront;
    } else if (accumulatedParams.waterfront === true) {
      // Filter was removed - explicitly set to false to clear the chip
      filterChanges.waterfront = false;
    }
    
    if (params.openHouse !== undefined) {
      filterChanges.openHouse = params.openHouse;
    } else if (accumulatedParams.openHouse === true) {
      // Filter was removed - explicitly set to false to clear the chip
      filterChanges.openHouse = false;
    }
    
    // Add missing parameter mappings
    if (params.parkingSpaces !== undefined) {
      filterChanges.parkingSpaces = params.parkingSpaces;
    }
    
    // Check if sqft parameters were removed (existed before but not in new params)
    if (accumulatedParams.sqftMin !== undefined && params.sqftMin === undefined) {
      filterChanges.sqftMin = undefined; // Explicitly remove
    }
    if (accumulatedParams.sqftMax !== undefined && params.sqftMax === undefined) {
      filterChanges.sqftMax = undefined; // Explicitly remove
    }
    
    // Apply new sqft parameters if they exist
    if (params.sqftMin !== undefined) {
      filterChanges.sqftMin = params.sqftMin;
    }
    if (params.sqftMax !== undefined) {
      filterChanges.sqftMax = params.sqftMax;
    }
    
    // Check if date parameters were removed (existed before but not in new params)
    if (accumulatedParams.listedSince !== undefined && params.listedSince === undefined) {
      filterChanges.listedSince = null; // Explicitly remove
    }
    if (accumulatedParams.listedTo !== undefined && params.listedTo === undefined) {
      filterChanges.listedTo = null; // Explicitly remove
    }
    
    // Apply new date parameters if they exist
    if (params.listedSince !== undefined) {
      filterChanges.listedSince = params.listedSince;
    }
    if (params.listedTo !== undefined) {
      filterChanges.listedTo = params.listedTo;
    }
    
    // Apply condo/townhouse specific filters (locker, maintenanceFee)
    // These filters only apply when searching for condos or townhouses
    const hasCondoOrTownhouse = params.homeType?.condo || params.homeType?.townhouse;
    
    if (params.homeType && !hasCondoOrTownhouse) {
      // Clear condo-specific filters when switching away from condos/townhouses
      filterChanges.additional = {
        condoOrTownhouse: {
          locker: 'any',
          maintenanceFee: null,
        },
      };
    } else if (params.additional?.condoOrTownhouse) {
      // Apply condo-specific filters only when searching for condos/townhouses
      filterChanges.additional = {
        condoOrTownhouse: {
          ...params.additional.condoOrTownhouse,
        },
      };
    }
    
    if (Object.keys(filterChanges).length > 0) {
      onFiltersChanged(filterChanges, 'chatbot');
      
      // Update AI search params in context with validated prices
      const validatedParams = { ...params };
      if (finalPriceMin !== undefined) {
        validatedParams.priceMin = finalPriceMin;
      } else {
        validatedParams.priceMin = undefined;
      }
      if (finalPriceMax !== undefined) {
        validatedParams.priceMax = finalPriceMax;
      } else {
        validatedParams.priceMax = undefined;
      }
      setAiSearchParams(validatedParams);
    }

    if (onMapLocationChange && (params.city || params.province)) {
      // Helper to get centroid from polygon API
      const getCentroidBySlug = async (slug: string): Promise<{ lat: number; lng: number } | null> => {
        const polygonsResponse = await fetchAreaPolygons({ slug });
        const primaryArea = polygonsResponse?.data && polygonsResponse.data.length > 0 ? polygonsResponse.data[0] : null;
        const centroidWkt = primaryArea?.centroid;
        if (typeof centroidWkt === 'string' && centroidWkt.length > 0) {
          const centroid = parseWktPoint(centroidWkt);
          return { lat: centroid.latitude, lng: centroid.longitude };
        }
        return null;
      };

      // Helper to DRY up state/country fallback behavior
      const tryStateOrCountryFallback = async (): Promise<MapApplyResult | undefined> => {
        // If we know the state/province but couldn't resolve a city, prefer state-level fallback
        if (!params.city && params.province) {
          // Build state slug with country code: "ny-us" format (not "new-york")
          const provinceCode = params.province.toLowerCase() as ProvinceOrStateCode;
          const countryCode = params.country ? params.country.toLowerCase() : countryCodeFromProvinceOrState(provinceCode);
          const stateSlug = countryCode ? `${provinceCode}-${countryCode}` : provinceOrStateSlugFromCode(provinceCode);
          if (stateSlug) {
            const centroid = await getCentroidBySlug(stateSlug);
            if (centroid) {
              onMapLocationChange({ lat: centroid.lat, lng: centroid.lng, zoom: 6 });
              usedStateFallback = true;
              const provinceCodeForFallback = provinceOrStateCodeFromString(params.province);
              if (provinceCodeForFallback) {
                stateCodeForFallback = provinceCodeForFallback;
              }
              return { usedStateFallback, stateCode: stateCodeForFallback, usedCountryFallback: false };
            }
          }
        }

        // Country centroids as a last resort
        if (params.country === CountryCodeList.CANADA) {
          onMapLocationChange(defaultCAMapCenter);
          return { usedStateFallback, stateCode: stateCodeForFallback, usedCountryFallback: true };
        } else if (params.country === CountryCodeList.UNITED_STATES) {
          onMapLocationChange(defaultUSMapCenter);
          return { usedStateFallback, stateCode: stateCodeForFallback, usedCountryFallback: true };
        }
      };

      // Build the best slug and resolve centroid
      let locationSlug = '';
      if (params.city && params.province) {
        const citySlug = params.city.toLowerCase().replace(/\s+/g, '-');
        const provinceSlug = params.province.toLowerCase().replace(/\s+/g, '-');
        locationSlug = `${citySlug}-${provinceSlug}`;
      } else if (params.city) {
        locationSlug = params.city.toLowerCase().replace(/\s+/g, '-');
      } else if (params.province) {
        // Build state slug with country code: "ny-us" format (not "new-york")
        const provinceCode = params.province.toLowerCase() as ProvinceOrStateCode;
        const countryCode = params.country ? params.country.toLowerCase() : countryCodeFromProvinceOrState(provinceCode);
        const stateSlug = countryCode ? `${provinceCode}-${countryCode}` : provinceOrStateSlugFromCode(provinceCode);
        if (stateSlug) {
          locationSlug = stateSlug;
          usedStateFallback = true;
          const provinceCodeForFallback = provinceOrStateCodeFromString(params.province);
          if (provinceCodeForFallback) {
            stateCodeForFallback = provinceCodeForFallback;
          }
        }
      }

      if (locationSlug) {
        const centroid = await getCentroidBySlug(locationSlug);

        // If city+state slug failed, try state-only fallback deterministically
        if (!centroid && params.province) {
          // Build state slug with country code: "ny-us" format (not "new-york")
          const provinceCode = params.province.toLowerCase() as ProvinceOrStateCode;
          const countryCode = params.country ? params.country.toLowerCase() : countryCodeFromProvinceOrState(provinceCode);
          const stateSlug = countryCode ? `${provinceCode}-${countryCode}` : provinceOrStateSlugFromCode(provinceCode);
        
          if (stateSlug) {
            const stateCentroid = await getCentroidBySlug(stateSlug);
            if (stateCentroid) {
              onMapLocationChange({ lat: stateCentroid.lat, lng: stateCentroid.lng, zoom: 6 });
              usedStateFallback = true;
              const provinceCodeForFallback = provinceOrStateCodeFromString(params.province);
              if (provinceCodeForFallback) {
                stateCodeForFallback = provinceCodeForFallback;
              }
              // Indicate that the city was not found
              const cityNotFound = params.city || undefined;
              return { usedStateFallback, stateCode: stateCodeForFallback, usedCountryFallback: false, cityNotFound };
            }
          }
        }

        if (centroid) {
          const isStateLevel = !params.city && Boolean(params.province);
          onMapLocationChange({
            lat: centroid.lat,
            lng: centroid.lng,
            zoom: isStateLevel ? ZOOM_STATE_LEVEL : ZOOM_DEFAULT,
          });
          return { usedStateFallback, stateCode: stateCodeForFallback, usedCountryFallback: false };
        }
      }

      const fallback = await tryStateOrCountryFallback();
      if (fallback) return fallback;
    }
    return { usedStateFallback, stateCode: stateCodeForFallback, usedCountryFallback: false };
  }, [onFiltersChanged, onMapLocationChange, accumulatedParams, setAiSearchParams]);

  // Check if there are any user-applied (non-default) filters
  const hasUserAppliedFilters = getActiveFilters(filters).length > 0;
  
  // Currently will not reset if the filters are changed without restarting the chatbot. 
  // Could be jarring to change the welcome message without user input?
  const welcomeMessageContext = hasUserAppliedFilters 
    ? 'with_filters'
    : 'no_filters';
  const welcomeMessageBody = t(LOCALE_COMMON_PATHS.SMART_CHAT_WELCOME_MESSAGE, {
    context: welcomeMessageContext,
    exampleCity,
    defaultValue: `Hello! I can help you find properties. Try saying something like "Show me 3 bedroom houses in ${exampleCity} under $800k"`,
  });

  // Function to get personalized welcome message for authenticated users
  // Returns { message, hasExistingMessages } to indicate if user already has conversation history
  const getPersonalizedWelcomeMessage = useCallback(async (): Promise<{ message: string; hasExistingMessages: boolean }> => {
    // Check if user has existing AI search params
    const hasExistingAiSearchParams = aiSearchParams && Object.keys(aiSearchParams).length > 0;
    
    // Only get location context if user doesn't have existing AI search params
    const currentLocation = !hasExistingAiSearchParams ? getCurrentLocationContext() : null;

    // If we have location context but no AI search params, add location to context
    if (currentLocation && !hasExistingAiSearchParams) {
      setAiSearchParams({ city: currentLocation });
    }

    if (!isAuthenticated || !user?.firstName) {
      // For unauthenticated users, return default message (no existing messages)
      return { message: welcomeMessageBody, hasExistingMessages: false };
    }
    
    try {
      // Fetch the latest conversation using the new Conversations API
      if (!user.jwt) {
        return { message: welcomeMessageBody, hasExistingMessages: false };
      }

      const conversationsResponse = await listConversations({
        jwt: user.jwt,
        limit: 1,
      });

      if (conversationsResponse.conversations.length > 0) {
        const latestConversationSummary = conversationsResponse.conversations[0];
        
        // Set the conversation ID for future messages
        setConversationId(latestConversationSummary.conversationId);

        // Fetch full conversation details to get metadata with SearchParametersDiff
        const fullConversation = await getConversation({
          jwt: user.jwt,
          conversationId: latestConversationSummary.conversationId,
        });

        // Extract search params from conversation metadata
        let searchParams: SearchParameters = {};
        if (fullConversation.metadata?.SearchParametersDiff) {
          try {
            searchParams = JSON.parse(fullConversation.metadata.SearchParametersDiff);
          } catch {
            console.warn('Failed to parse SearchParametersDiff from conversation metadata');
          }
        }

        // Fetch messages for the conversation
        const messagesResponse = await listMessages({
          jwt: user.jwt,
          conversationId: latestConversationSummary.conversationId,
          limit: 10,
          order: 'asc',
        });

        // Populate chat messages from stored messages
        const hasExistingMessages = messagesResponse.messages.length > 0;
        if (hasExistingMessages) {
          const chatMessages: ChatMessageUI[] = messagesResponse.messages.map(msg => ({
            id: msg.messageId,
            role: msg.role === Role.USER ? 'user' : 'assistant',
            content: msg.content,
            timestamp: new Date(msg.createdAt),
            savedMessageId: msg.messageId,
            conversationId: latestConversationSummary.conversationId,
            metadata: msg.metadata,
          }));
          setMessages(chatMessages);
        }

        // Apply search params if they exist
        if (Object.keys(searchParams).length > 0) {
          setAccumulatedParams(searchParams);
          setAiSearchParams(searchParams);
          
          // Apply the same filter changes as when processing chat messages
          await applySearchParams(searchParams);
          
          // Trigger a search with the last search parameters
          if (onSearchParamsChange) {
            onSearchParamsChange(searchParams);
          }
        }

        // Return appropriate welcome message based on whether user has existing messages
        const name = user.firstName ? ` ${user.firstName}` : '';
        
        // If user has existing conversation history, show "welcome back" message
        if (hasExistingMessages) {
          return { 
            message: t(LOCALE_COMMON_PATHS.SMART_CHAT_WELCOME_BACK_MESSAGE, { 
              defaultValue: "Welcome back{{name}}! How's your home search going? Let's pick up from where you left off.",
              name,
            }),
            hasExistingMessages: true,
          };
        }
        
        // Otherwise show regular welcome message
        return { 
          message: t(LOCALE_COMMON_PATHS.SMART_CHAT_WELCOME_MESSAGE, { 
            exampleCity,
            defaultValue: `Hello${name}! I can help you find properties. Try saying something like "Show me 3 bedroom houses in ${exampleCity} under $800k"`,
          }),
          hasExistingMessages: false,
        };
      }

      // No existing conversations, return default welcome message
      const name = user.firstName ? ` ${user.firstName}` : '';
      return { 
        message: t(LOCALE_COMMON_PATHS.SMART_CHAT_WELCOME_MESSAGE, { 
          exampleCity,
          defaultValue: `Hello${name}! I can help you find properties. Try saying something like "Show me 3 bedroom houses in ${exampleCity} under $800k"`,
        }),
        hasExistingMessages: false,
      };
    } catch (error) {
      console.error('Error getting personalized welcome message:', error);
      return { message: welcomeMessageBody, hasExistingMessages: false };
    }
  }, [isAuthenticated, user, aiSearchParams, welcomeMessageBody, getCurrentLocationContext, setAiSearchParams, applySearchParams, onSearchParamsChange, t, setMessages, exampleCity]);

  // Initialize with welcome message using useEffect
  useEffect(() => {
    if (messages.length === 0 && !hasInitialized.current) {
      hasInitialized.current = true;
      const initializeWelcomeMessage = async () => {
        try {
          const { message: messageContent } = await getPersonalizedWelcomeMessage();
          
          // Create welcome message
          const welcomeMessage: ChatMessageUI = {
            id: generateMessageId(),
            role: 'assistant',
            content: messageContent,
            timestamp: new Date(),
            isWelcomeMessage: true,
          };
          setMessages(prev => [...prev, welcomeMessage]);
        } catch (error) {
          console.error('Error initializing welcome message:', error);
          // Fallback to default message
          const welcomeMessage: ChatMessageUI = {
            id: generateMessageId(),
            role: 'assistant',
            content: welcomeMessageBody,
            timestamp: new Date(),
            isWelcomeMessage: true,
          };
          setMessages(prev => [...prev, welcomeMessage]);
        }
      };

      initializeWelcomeMessage();
    }
  }, [messages.length, getPersonalizedWelcomeMessage, welcomeMessageBody, isAuthenticated, getCurrentLocationContext, generateMessageId]);

  // Update welcome message when user logs in or out
  useEffect(() => {
    const justLoggedIn = !prevIsAuthenticated.current && isAuthenticated;
    const justLoggedOut = prevIsAuthenticated.current && !isAuthenticated;
    const hasNoUserMessages = !messages.some(m => m.role === 'user');
    
    // Handle logout - revert to default welcome message
    if (justLoggedOut) {
      if (hasNoUserMessages) {
        const defaultMessage: ChatMessageUI = {
          id: generateMessageId(),
          role: 'assistant',
          content: welcomeMessageBody,
          timestamp: new Date(),
          isWelcomeMessage: true,
        };
        setMessages([defaultMessage]);
      }

      prevIsAuthenticated.current = isAuthenticated;
      return;
    }
    
    // Handle login - load chat history
    if (justLoggedIn && hasNoUserMessages) {
      const loadChatHistory = async () => {
        try {
          await getPersonalizedWelcomeMessage();
        } catch (error) {
          console.error('Error loading chat history after login:', error);
        }
      };
      
      loadChatHistory();
    }
    
    prevIsAuthenticated.current = isAuthenticated;
  }, [isAuthenticated, messages, getPersonalizedWelcomeMessage, welcomeMessageBody, generateMessageId]);

  const sessionStartTimeRef = useRef(Date.now());
  const messagesRef = useRef(messages);
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    // Track session end when chatbot component unmounts (UI leaves AI search)
    const trackSessionEnd = () => {
      if (hasTrackedRef.current) return;
      hasTrackedRef.current = true;

      const sessionStartTime = sessionStartTimeRef.current;
      const sessionDurationSeconds = Math.floor(
        (Date.now() - sessionStartTime) / 1000
      );
      
      trackSmartchatSessionEnd(sessionDurationSeconds);
    };

    window.addEventListener('beforeunload', trackSessionEnd);
    window.addEventListener('pagehide', trackSessionEnd);
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        trackSessionEnd();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', trackSessionEnd);
      window.removeEventListener('pagehide', trackSessionEnd);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      hasTrackedRef.current = false;
      trackSessionEnd();
    };
  }, []) 

  const processQuery = useCallback(async (
    userQuery: string,
    currentMessages?: ChatMessageUI[]
  ): Promise<{ searchParams: SearchParameters; message: string; needsClarification: boolean; clarification?: string; followUpSuggestions?: string[] }> => {
    try {
      // Processing conversational search query

      if (containsCompetitorName(userQuery)) {
        throw new CompetitorError('Query contains competitor name');
      }
      
      // Use currentMessages if provided (includes the new user message), otherwise fall back to state
      // Get the last 5 messages (both user and assistant) BEFORE the current user query
      // This gives context to the AI for understanding simple responses like "yes"
      const messagesToUse = currentMessages || messages;
      // If currentMessages is provided, it includes the new user message as the last item
      // We want the 5 messages BEFORE the current user query, so we exclude the last item
      const recentMessages = currentMessages
        ? messagesToUse.slice(-6, -1) // Exclude the last item (current user query)
        : messagesToUse.slice(-5); // If using state, just take last 5 (current query not in state yet)
      
      const formattedRecentMessages = recentMessages
        .map(msg => ({
          role: msg.role,
          content: msg.content,
        }));
      
      // Merge accumulatedParams with current filters to ensure AI knows about manually removed filters
      // This prevents removed filter chips from reappearing when user adds new requirements
      const currentParams = mergeSearchParamsWithFilter(accumulatedParams, filters);

      // First try the deterministic parser before going to the AI. Only
      // short-circuit when `unparsed` is null — leftover tokens mean the
      // parser missed something the AI may need full conversation context
      // to interpret, so we fall through rather than apply a partial answer.
      const fastPathResult = await parseNaturalLanguageQueryIntoFilters(userQuery);
      if (fastPathResult && fastPathResult.unparsed === null) {
        const mergedFilters = mergeFiltersBasedOnIntent('add_filter', currentParams, fastPathResult.filters);
        setAccumulatedParams(mergedFilters);
        return {
          searchParams: mergedFilters,
          message: t(LOCALE_COMMON_PATHS.SMART_CHAT_UNDERSTAND_SOME_REQUIREMENTS, { defaultValue: 'Sounds good. Let me know if you need to add more details.' }),
          needsClarification: false,
        };
      }

      const requestBody = {
        query: sub250Chars(userQuery),
        currentParams: currentParams,
        tenantId: tenantId,
        recentMessages: formattedRecentMessages.length > 0 ? formattedRecentMessages : undefined,
      };
      
      const options: FetchOptions = {
        method: HttpRequestMethodTypes.POST,
        headers: {
          ...JSON_CONTENT_TYPE_HEADER,
          ...(user?.jwt && { Authorization: `Bearer ${user.jwt}` }),
        },
        body: JSON.stringify(requestBody),
      };

      const response: Response = await fetchWithRetry('/api/conversational-search', options);

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const nlpResponse = await response.json();
      
      if (nlpResponse.searchParams && Object.keys(nlpResponse.searchParams).length > 0) {
        // Validate and fix price range before setting accumulatedParams
        const validatedParams = { ...nlpResponse.searchParams };
        
        // If both priceMin and priceMax exist, ensure min < max
        // If min > max, clear priceMin (user likely said "under X")
        if (validatedParams.priceMin !== undefined && validatedParams.priceMax !== undefined) {
          if (validatedParams.priceMin > validatedParams.priceMax) {
            validatedParams.priceMin = undefined; // Clear invalid min
          }
        }
        
        // Merge AI response with current filters to ensure manually removed filters stay removed
        // Strategy: Start with AI response, then apply removals for filters that were previously set
        // This preserves AI's new filters while respecting manual removals
        const mergedParams = { ...validatedParams };
        
        // Helper to check if AI changed a filter value (different from what was in accumulatedParams)
        const aiChangedFilter = (key: keyof SearchParameters): boolean => {
          if (validatedParams[key] === undefined) return false; // AI didn't set it
          if (accumulatedParams[key] === undefined) return true; // AI set a new filter
          return JSON.stringify(validatedParams[key]) !== JSON.stringify(accumulatedParams[key]);
        };
        
        // For string filters (bedrooms, bathrooms, parkingSpaces): '0+' or undefined means removed
        if (accumulatedParams.bedrooms && 
            (filters.bedrooms === '0+' || filters.bedrooms === undefined) &&
            !aiChangedFilter('bedrooms')) {
          mergedParams.bedrooms = filters.bedrooms;
        }
        if (accumulatedParams.bathrooms && 
            (filters.bathrooms === '0+' || filters.bathrooms === undefined) &&
            !aiChangedFilter('bathrooms')) {
          mergedParams.bathrooms = filters.bathrooms;
        }
        if (accumulatedParams.parkingSpaces && 
            (filters.parkingSpaces === '0+' || filters.parkingSpaces === undefined) &&
            !aiChangedFilter('parkingSpaces')) {
          mergedParams.parkingSpaces = filters.parkingSpaces;
        }
        
        // For number filters (price, sqft): undefined/null means removed
        if (accumulatedParams.priceMin !== undefined && 
            (filters.priceMin === undefined || filters.priceMin === null) &&
            !aiChangedFilter('priceMin')) {
          mergedParams.priceMin = undefined;
        }
        if (accumulatedParams.priceMax !== undefined && 
            (filters.priceMax === undefined || filters.priceMax === null) &&
            !aiChangedFilter('priceMax')) {
          mergedParams.priceMax = undefined;
        }
        if (accumulatedParams.sqftMin !== undefined && 
            (filters.sqftMin === undefined || filters.sqftMin === null) &&
            !aiChangedFilter('sqftMin')) {
          mergedParams.sqftMin = undefined;
        }
        if (accumulatedParams.sqftMax !== undefined && 
            (filters.sqftMax === undefined || filters.sqftMax === null) &&
            !aiChangedFilter('sqftMax')) {
          mergedParams.sqftMax = undefined;
        }
        
        // For boolean filters: false means removed (only if it was previously true)
        if (accumulatedParams.garage === true && 
            (filters.garage === false || filters.garage === undefined) &&
            !aiChangedFilter('garage')) {
          mergedParams.garage = false;
        }
        if (accumulatedParams.pool === true && 
            (filters.pool === false || filters.pool === undefined) &&
            !aiChangedFilter('pool')) {
          mergedParams.pool = false;
        }
        if (accumulatedParams.fireplace === true && 
            (filters.fireplace === false || filters.fireplace === undefined) &&
            !aiChangedFilter('fireplace')) {
          mergedParams.fireplace = false;
        }
        if (accumulatedParams.waterfront === true && 
            (filters.waterfront === false || filters.waterfront === undefined) &&
            !aiChangedFilter('waterfront')) {
          mergedParams.waterfront = false;
        }
        if (accumulatedParams.openHouse === true && 
            (filters.openHouse === false || filters.openHouse === undefined) &&
            !aiChangedFilter('openHouse')) {
          mergedParams.openHouse = false;
        }
        
        // For homeType: Handle AI's value carefully
        // The AI's homeType gets merged in nlp-processor, so we need to detect if AI wanted to restrict types
        if (validatedParams.homeType && Object.keys(validatedParams.homeType).length > 0) {
          // Check if AI's homeType is more restrictive than accumulatedParams (fewer types selected)
          const aiSelectedTypes = Object.values(validatedParams.homeType).filter(v => v === true).length;
          const accumulatedSelectedTypes = accumulatedParams.homeType 
            ? Object.values(accumulatedParams.homeType).filter(v => v === true).length 
            : 0;
          
          // Check if AI's selections are a strict subset (all AI's true values were true in accumulated, but fewer total)
          const aiTrueKeys = Object.keys(validatedParams.homeType).filter(k => validatedParams.homeType?.[k as keyof typeof validatedParams.homeType] === true);
          const wasRestrictive = accumulatedParams.homeType 
            ? aiTrueKeys.every(k => accumulatedParams.homeType?.[k as keyof typeof accumulatedParams.homeType] === true) &&
              aiSelectedTypes < accumulatedSelectedTypes
            : false;
          
          // If AI is restricting types (fewer selected) or this is a new selection, ensure complete object
          if (wasRestrictive || accumulatedSelectedTypes === 0 || aiSelectedTypes < accumulatedSelectedTypes) {
            // AI is restricting types - create a complete homeType object with only AI's selections
            mergedParams.homeType = {
              house: false,
              condo: false,
              townhouse: false,
              farm: false,
              land: false,
              commercial: false,
              ...validatedParams.homeType, // Override with AI's selections
            };
          }
          // Otherwise, use the merged result (AI added types, not restricted)
        } else if (accumulatedParams.homeType && 
                   filters.homeType && 
                   Object.values(filters.homeType).every(v => v === false)) {
          // AI didn't set homeType, but user removed it - apply removal
          mergedParams.homeType = filters.homeType;
        }
        
        setAccumulatedParams(mergedParams);
        
        // Update the response to use merged params so they're applied correctly
        nlpResponse.searchParams = mergedParams;
      }
      
      let message = '';
      
      if (nlpResponse.needsClarification) {
        message = nlpResponse.clarification || t(LOCALE_COMMON_PATHS.SMART_CHAT_NEED_MORE_INFORMATION, { defaultValue: 'I need more information to help you find properties.' });
      } else if (nlpResponse.message) {
        // Use AI's generated message
        message = nlpResponse.message;
      } else {
        // Fallback when the AI returned no custom message and no clarification
        // request. Distinguish between "filters were updated, just no message"
        // (use the affirming default) and "nothing actionable was extracted"
        // (be honest about it so the user knows to rephrase). Compare the
        // returned params key-by-key against `currentParams` so a partial
        // echo (e.g. just `homeType` matching the existing value) is still
        // correctly classified as "nothing new".
        const aiSearchParams = nlpResponse.searchParams as SearchParameters | undefined;
        const aiExtractedSomethingNew = !!aiSearchParams
          && Object.keys(aiSearchParams).length > 0
          && Object.keys(aiSearchParams).some(key => {
            const next = aiSearchParams[key as keyof SearchParameters];
            const prev = currentParams[key as keyof SearchParameters];
            return !isEqual(next, prev);
          });
        message = aiExtractedSomethingNew
          ? t(LOCALE_COMMON_PATHS.SMART_CHAT_UNDERSTAND_SOME_REQUIREMENTS, { defaultValue: 'Sounds good. Let me know if you need to add more details.' })
          : t(LOCALE_COMMON_PATHS.SMART_CHAT_NEED_MORE_INFORMATION, { defaultValue: "I'm not sure how to act on that. Could you rephrase your request — for example, a price range, location, or property type?" });
      }
      
      return {
        searchParams: nlpResponse.searchParams,
        message,
        needsClarification: nlpResponse.needsClarification,
        clarification: nlpResponse.clarification,
        followUpSuggestions: nlpResponse.followUpSuggestions,
      };
    } catch (error) {
      console.error('❌ Error in processQuery:', error);
      
      if (!(error instanceof CompetitorError)) {
        trackSmartchatError();
      }
      
      if (error instanceof CompetitorError) {
        return {
          searchParams: {},
          message: t(LOCALE_COMMON_PATHS.SMART_CHAT_COMPETITOR_NAME_ERROR, { defaultValue: 'This chat is designed for use on eXp Realty websites only. Please enter information about your home search.' }),
          needsClarification: true,
          clarification: t(LOCALE_COMMON_PATHS.SMART_CHAT_COMPETITOR_NAME_ERROR, { defaultValue: 'This chat is designed for use on eXp Realty websites only. Please enter information about your home search.' }),
          followUpSuggestions: [t(LOCALE_COMMON_PATHS.SMART_CHAT_PLEASE_REPHRASE_REQUEST, { defaultValue: 'Please rephrase your request' })],
        };
      }
      return {
        searchParams: {},
        message: 'I encountered an error processing your request. Please try again.',
        needsClarification: true,
        clarification: 'I encountered an error processing your request. Please try again.',
        followUpSuggestions: ['Please rephrase your request'],
      };
    }
  }, [accumulatedParams, filters, tenantId, user?.jwt, t, messages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Notify the parent whenever the waiting-for-AI state changes so it can
  // synchronise external UI (e.g. hide suggestion chips during a request).
  // Treat the short window between filter application and the follow-up listing
  // count message as still "loading" so chips don't flash in prematurely.
  useEffect(() => {
    onLoadingChange?.(isLoading || isAwaitingFinalMessage);
  }, [isLoading, isAwaitingFinalMessage, onLoadingChange]);

  useEffect(() => {
    return () => {
      if (awaitingFinalMessageTimeoutRef.current) {
        clearTimeout(awaitingFinalMessageTimeoutRef.current);
      }
    };
  }, []);

  // Sync filter changes (e.g., chip removals) to conversation metadata
  // This ensures removed filters stay removed after page refresh
  useEffect(() => {
    if (!isAuthenticated || !user?.jwt || !conversationId) return;
    
    // Merge current filters with accumulatedParams to capture removals
    const mergedParams = mergeSearchParamsWithFilter(accumulatedParams, filters);
    
    // Only update if there's a meaningful change
    if (JSON.stringify(mergedParams) !== JSON.stringify(accumulatedParams)) {
      setAccumulatedParams(mergedParams);
      
      // Persist to backend
      updateConversation({
        jwt: user.jwt,
        conversationId: conversationId,
        metadata: {
          SearchParametersDiff: JSON.stringify(mergedParams),
          SourceTenant: tenantId,
        },
      }).catch(err => console.error('Failed to persist filter changes:', err));
    }
  }, [filters, isAuthenticated, user?.jwt, conversationId, accumulatedParams, tenantId]);

  const handleSendMessage = useCallback(async () => {
    if (!inputValue.trim() || isLoading) {
      return;
    }

    // Store the input value before clearing it
    const queryToProcess = inputValue.trim();
    const messageTimestamp = new Date();

    const userMessageId = generateMessageId();
    const userMessage: ChatMessageUI = {
      id: userMessageId,
      role: 'user',
      content: queryToProcess,
      timestamp: messageTimestamp,
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    // Track new user message event
    trackSmartchatNewUserMessage();

    // Create conversation if needed and save user message
    let currentConversationId = conversationId;
    if (isAuthenticated && user?.jwt) {
      try {
        // Create a new conversation if one doesn't exist
        if (!currentConversationId) {
          const conversationResponse = await createConversation({
            jwt: user.jwt,
            title: 'Property Search',
            metadata: {
              SourceTenant: tenantId,
            },
          });
          currentConversationId = conversationResponse.conversationId;
          setConversationId(currentConversationId);
        }

        // Save user message to the conversation
        if (currentConversationId) {
          createMessage({
            jwt: user.jwt,
            conversationId: currentConversationId,
            content: queryToProcess,
            role: Role.USER,
            createdAt: messageTimestamp,
          }).then(response => {
            // Attach the message ID from API response 
            setMessages(prev => prev.map(msg => {
              if (msg.id === userMessageId)
                return { ...msg, savedMessageId: response.messageId };

              else
                return msg;
            }));
          }).catch(error => {
            console.error('Error saving user message:', error);
          });
        }
      } catch (error) {
        console.error('Error creating conversation:', error);
      }
    }

    try {
      // Construct messages array including the new user message for context
      // This ensures processQuery has access to all messages including the one just added
      const messagesWithNewUser = [...messages, userMessage];
      // Snapshot the effective filter state at query time so we can later
      // detect whether the AI/parser returned a meaningful change. If it
      // just echoes the current state, no listing-count follow-up will
      // fire and we must surface the standard reply directly.
      const paramsBeforeQuery = mergeSearchParamsWithFilter(accumulatedParams, filters);
      const result = await processQuery(queryToProcess, messagesWithNewUser);

      // Block location changes if disableLocationSearch is enabled (e.g. hybrid page)
      let locationBlocked = false;
      if (disableLocationSearch && result.searchParams) {
        const hasLocationParam = LOCATION_PARAM_KEYS.some(key => result.searchParams[key] !== undefined);
        if (hasLocationParam) {
          locationBlocked = true;
          // Strip location params from search results so they are not applied
          for (const key of LOCATION_PARAM_KEYS) {
            delete result.searchParams[key];
          }
          // Also strip from accumulated params state to prevent persistence
          setAccumulatedParams(prev => {
            const updated = { ...prev };
            for (const key of LOCATION_PARAM_KEYS) {
              delete updated[key];
            }
            return updated;
          });
        }
      }

      // When location search is disabled (e.g. hybrid page), the AI may flag
      // `needsClarification: true` purely to ask the user for a location. If
      // the AI still extracted usable non-location filters (home type, price,
      // bedrooms, etc.), apply them instead of prompting for a location the
      // user is not expected to provide here.
      if (disableLocationSearch && result.needsClarification && result.searchParams) {
        const hasNonLocationFilter = Object.keys(result.searchParams).some(
          key => !LOCATION_PARAM_KEYS.includes(key as keyof SearchParameters)
            && result.searchParams[key as keyof SearchParameters] !== undefined
        );
        if (hasNonLocationFilter) {
          result.needsClarification = false;
        }
      }

      const assistantTimestamp = new Date();
      
      // Store suggestions in ai response's metadata.
      const metadata: Record<string, string> = {};
      if (result.followUpSuggestions && result.followUpSuggestions.length > 0) {
        metadata.suggestions = JSON.stringify(result.followUpSuggestions);
      }
      
      const assistantMessageId = generateMessageId();
      const assistantMessage: ChatMessageUI = {
        id: assistantMessageId,
        role: 'assistant',
        content: result.message,
        timestamp: assistantTimestamp,
        metadata: Object.keys(metadata).length > 0 ? metadata: null,
      };

      // When the caller opts into `onlyShowListingCountResponse`, we normally
      // suppress this default assistant message because it will be replaced by
      // the follow-up "X listings found" message after filters apply. However,
      // if no filters will be applied (e.g. unprocessable query, clarification
      // request, or empty params) there is no follow-up, so we must still show
      // this message — otherwise the chat appears to silently ignore the user.
      //
      // We also need to detect when `result.searchParams` is non-empty but
      // semantically identical to the state that was already in effect (the AI
      // can echo `currentParams` back when it has nothing to add). In that
      // case the search count won't change and the listing-count effect won't
      // fire — suppressing the standard reply would leave the chat silent.
      // Compare key-by-key against the snapshot (and use deep equality so
      // partial or reordered echoes — e.g. the same `homeType` with shuffled
      // keys — are still recognized as a no-op).
      const filtersWillChange = !!result.searchParams
        && Object.keys(result.searchParams).length > 0
        && Object.keys(result.searchParams).some(key => {
          const next = result.searchParams[key as keyof SearchParameters];
          const prev = paramsBeforeQuery[key as keyof SearchParameters];
          return !isEqual(next, prev);
        });
      const willApplyFilters = !result.needsClarification && filtersWillChange;

      if (!onlyShowListingCountResponse || !willApplyFilters) {
        setMessages(prev => [...prev, assistantMessage]);
      }

   
      if (isAuthenticated && user?.jwt && currentConversationId) {
        createMessage({
          jwt: user.jwt,
          conversationId: currentConversationId,
          content: result.message,
          role: Role.ASSISTANT,
          createdAt: assistantTimestamp,
          metadata: Object.keys(metadata).length > 0 ? metadata : null,
        }).then(response => {
          // Attach the message ID from API response 
          setMessages(prev => prev.map(msg => {
            if (msg.id === assistantMessageId)
              return { ...msg, savedMessageId: response.messageId, metadata: response.metadata };

            else
              return msg;
          }));
        }).catch(error => {
          console.error('Error saving assistant message:', error);
        });
      }
      
      
      if (!result.needsClarification && result.searchParams && Object.keys(result.searchParams).length > 0) {
        // Determine which filter was last applied by comparing with previous params
        const prevParams = previousSearchParamsRef.current;
        const newParams = result.searchParams;
        
        // Find the most recently added/changed filter
        // Check all changes and prioritize price/budget as it's often specified last
        const changedFilters: string[] = [];
        
        if (newParams.priceMin !== prevParams.priceMin || newParams.priceMax !== prevParams.priceMax) {
          changedFilters.push('price');
        }
        if (newParams.bedrooms !== prevParams.bedrooms) {
          changedFilters.push('bedrooms');
        }
        if (newParams.bathrooms !== prevParams.bathrooms) {
          changedFilters.push('bathrooms');
        }
        if (newParams.sqftMin !== prevParams.sqftMin || newParams.sqftMax !== prevParams.sqftMax) {
          changedFilters.push('square footage');
        }
        if (JSON.stringify(newParams.homeType) !== JSON.stringify(prevParams.homeType)) {
          changedFilters.push('property type');
        }
        if (newParams.pool !== prevParams.pool) {
          changedFilters.push('pool');
        }
        if (newParams.garage !== prevParams.garage) {
          changedFilters.push('garage');
        }
        if (newParams.waterfront !== prevParams.waterfront) {
          changedFilters.push('waterfront');
        }
        if (newParams.fireplace !== prevParams.fireplace) {
          changedFilters.push('fireplace');
        }
        if (newParams.openHouse !== prevParams.openHouse) {
          changedFilters.push('open house');
        }
        
        // Prioritize price if it changed, otherwise use the first changed filter
        const lastFilter = changedFilters.includes('price') ? 'price' : changedFilters[0] || null;
        
        lastAppliedFilterRef.current = lastFilter;
        
        const applyInfo = await applySearchParams(result.searchParams);
        
        trackSmartchatFilterApplied();
        
        // Store search params to check for zero results later (after search completes)
        lastSearchParamsRef.current = result.searchParams;
        setIsAwaitingFinalMessage(true);
        // Safety fallback: if totalListingsCount doesn't change (e.g. the new filters
        // yielded the same count), the follow-up effect won't fire. Release the
        // awaiting flag after a short grace period so external UI doesn't get stuck.
        if (awaitingFinalMessageTimeoutRef.current) {
          clearTimeout(awaitingFinalMessageTimeoutRef.current);
        }
        awaitingFinalMessageTimeoutRef.current = setTimeout(() => {
          setIsAwaitingFinalMessage(false);
          awaitingFinalMessageTimeoutRef.current = null;
        }, 4000);
        
        // Update previous params for next comparison
        previousSearchParamsRef.current = { ...result.searchParams };
        
        // Update conversation with new search parameters
        if (isAuthenticated && user?.jwt && currentConversationId) {
          updateConversation({
            jwt: user.jwt,
            conversationId: currentConversationId,
            metadata: {
              SearchParametersDiff: JSON.stringify(result.searchParams),
              SourceTenant: tenantId,
            },
          }).catch(error => {
            console.error('Error updating conversation with search params:', error);
          });
        }
        
        // If fallback was used, proactively ask for clarification to refine the search
        if (applyInfo?.usedStateFallback || applyInfo?.usedCountryFallback) {
          const stateCodeFromResult = result.searchParams?.province ? provinceOrStateCodeFromString(result.searchParams.province) : null;
          const stateCode = applyInfo?.stateCode || stateCodeFromResult || undefined;
          const stateName = stateCode ? provinceOrStateNameFromCode(stateCode) : undefined;
          
          // If a city was attempted but not found, provide a specific message
          let fallbackClarification: string;
          if (applyInfo?.cityNotFound && stateName) {
            fallbackClarification = `I couldn't find a polygon for ${applyInfo.cityNotFound}. I've centered the map on ${stateName} instead. Would you like me to search a specific city in ${stateName}?`;
          } else if (stateName) {
            fallbackClarification = t(LOCALE_COMMON_PATHS.SMART_CHAT_NEED_MORE_INFORMATION, {
              defaultValue: `I couldn't find an exact match for that area. I've centered on ${stateName}. Would you like me to search a specific city in ${stateName}?`,
            });
          } else {
            fallbackClarification = t(LOCALE_COMMON_PATHS.SMART_CHAT_NEED_MORE_INFORMATION, {
              defaultValue: 'I couldn\'t find an exact match for that area. Which city or state should I search in?',
            });
          }
          
          const clarificationMessage: ChatMessageUI = {
            id: generateMessageId(),
            role: 'assistant',
            content: fallbackClarification,
            timestamp: new Date(),
          };
          if (!onlyShowListingCountResponse) {
            setMessages(prev => [...prev, clarificationMessage]);
          }
        }
        onSearchParamsChange?.(result.searchParams);
      } else if (result.needsClarification && result.searchParams && result.searchParams.province && !result.searchParams.city) {
        // Clarification needed, but we have a state-level grounding. Center map on the state.
        await applySearchParams({
          province: result.searchParams.province,
          country: result.searchParams.country,
        });
      }

      // Show location restriction message if location params were blocked
      if (locationBlocked) {
        const locationBlockedMessage: ChatMessageUI = {
          id: generateMessageId(),
          role: 'assistant',
          content: 'I\'m unable to change the search location from the chat. Please use the search bar at the top of the page to update your location.',
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, locationBlockedMessage]);
      }
      
    } catch (error) {
      console.error('Error in handleSendMessage:', error);
      trackSmartchatError();
      
      const errorMessage: ChatMessageUI = {
        id: generateMessageId(),
        role: 'assistant',
        content: t(LOCALE_COMMON_PATHS.SMART_CHAT_ERROR_PROCESSING_REQUEST, { defaultValue: 'Sorry, I encountered an error processing your request. Please try again.' }),
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [inputValue, isLoading, processQuery, applySearchParams, onSearchParamsChange, t, conversationId, isAuthenticated, user?.jwt, tenantId, generateMessageId, messages, disableLocationSearch, onlyShowListingCountResponse, accumulatedParams, filters]);

  // Show listing count after a filter change from chat
  // Handles both zero results (with suggestion) and positive results
  useEffect(() => {
    if (totalListingsCount == null || !lastSearchParamsRef.current) {
      return;
    }

    // Add a small delay to ensure the search has been applied and results are updated
    const timeoutId = setTimeout(() => {
      if (!lastSearchParamsRef.current) return;

      const params = lastSearchParamsRef.current;
      const lastFilter = lastAppliedFilterRef.current;

      if (totalListingsCount === 0) {
        // ── Zero results — provide a helpful suggestion ──
        let locationStr = '';
        if (params.city && params.province) {
          locationStr = `${params.city}, ${params.province}`;
        } else if (params.city) {
          locationStr = params.city;
        } else if (params.province) {
          locationStr = params.province;
        }

        const zeroResultsMessage = locationStr
          ? `0 listings found matching your criteria in ${locationStr}`
          : '0 listings found matching your criteria';

        let suggestionMessage = '';
        if (lastFilter) {
          suggestionMessage = `Do you want to look in some other city or try removing the ${lastFilter} filter?`;
        } else if (params.priceMax || params.priceMin) {
          suggestionMessage = 'Do you want to look in some other city or try removing the price filter?';
        } else if (params.bedrooms && params.bedrooms !== '0+') {
          suggestionMessage = 'Do you want to look in some other city or try removing the bedrooms filter?';
        } else if (params.bathrooms && params.bathrooms !== '0+') {
          suggestionMessage = 'Do you want to look in some other city or try removing the bathrooms filter?';
        } else if (params.homeType && Object.values(params.homeType).filter(Boolean).length === 1) {
          const selectedType = Object.keys(params.homeType).find(key => params.homeType?.[key as keyof typeof params.homeType]);
          if (selectedType) {
            const typeLabel = selectedType === 'house' ? 'houses' : selectedType === 'condo' ? 'condos' : selectedType;
            suggestionMessage = `Do you want to look in some other city or try removing the ${typeLabel} filter?`;
          }
        } else if (params.sqftMin || params.sqftMax) {
          suggestionMessage = 'Do you want to look in some other city or try removing the square footage filter?';
        } else {
          suggestionMessage = 'Do you want to try a different location or adjust your filters?';
        }

        const zeroResultsChatMessage: ChatMessageUI = {
          id: generateMessageId(),
          role: 'assistant',
          content: `${zeroResultsMessage}. ${suggestionMessage}`,
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, zeroResultsChatMessage]);
      } else {
        // ── Positive results — tell the user how many listings were found ──
        const countStr = totalListingsCount.toLocaleString();
        const resultsMessage: ChatMessageUI = {
          id: generateMessageId(),
          role: 'assistant',
          content: `${countStr} listings found! Any other requirements?`,
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, resultsMessage]);
      }

      // Clear refs to avoid duplicate messages
      lastSearchParamsRef.current = null;
      lastAppliedFilterRef.current = null;
      setIsAwaitingFinalMessage(false);
      if (awaitingFinalMessageTimeoutRef.current) {
        clearTimeout(awaitingFinalMessageTimeoutRef.current);
        awaitingFinalMessageTimeoutRef.current = null;
      }
    }, 500); // Small delay to ensure search has completed

    return () => clearTimeout(timeoutId);
  }, [totalListingsCount, t, generateMessageId]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  // Clear only local chat state (messages, filters, etc.) without touching database
  const clearLocalChatState = useCallback(() => {
    // Clear AI search params from context
    setAiSearchParams({});

    // Reset chat state
    setAccumulatedParams({});
    setMessages([]);
    messagesRef.current = [];
    setConversationId(null);

    // Clear any in-flight listing-count follow-up state so the parent's loading
    // signal doesn't remain stuck after a manual chat clear.
    lastSearchParamsRef.current = null;
    lastAppliedFilterRef.current = null;
    setIsAwaitingFinalMessage(false);
    if (awaitingFinalMessageTimeoutRef.current) {
      clearTimeout(awaitingFinalMessageTimeoutRef.current);
      awaitingFinalMessageTimeoutRef.current = null;
    }

    sessionStartTimeRef.current = Date.now();
    hasTrackedRef.current = false;

    // Clear applied filters if callback is provided
    if (onFiltersChanged) {
      // Reset to default filter state by clearing conversational search applied filters
      // Use proper "no filter" values to avoid empty chips in UI
      const resetFilters: PartialDeep<Filter> = {
        bedrooms: '0+', // '0+' means "Any Beds" - won't show as chip
        bathrooms: '0+', // '0+' means "Any Baths" - won't show as chip
        priceMin: null,
        priceMax: null,
        homeType: {
          house: true, // Reset to all types selected (default state)
          condo: true,
          townhouse: true,
          farm: true,
          land: true,
          commercial: true,
        },
        garage: false,
        pool: false,
        fireplace: false,
        waterfront: false,
        openHouse: false,
        parkingSpaces: '0+', // '0+' means no parking requirement - won't show as chip
        sqftMin: null,
        sqftMax: null,
        listedSince: null,
        listedTo: null,
        status: 'available', // Reset to active listings (default state)
        rental: false, // Reset to for sale (default state)
      };
      
      onFiltersChanged(resetFilters, 'chatbot-clear');
    }
    
    // Reset welcome message
    const welcomeMessage: ChatMessageUI = {
      id: generateMessageId(),
      role: 'assistant',
      content: t(LOCALE_COMMON_PATHS.SMART_CHAT_WELCOME_MESSAGE, {
        exampleCity,
        defaultValue: `Hello! I can help you find properties. Try saying something like "Show me 3 bedroom houses in ${exampleCity} under $800k"`,
      }),
      timestamp: new Date(),
      isWelcomeMessage: true,
    };
    setMessages([welcomeMessage]);
  }, [onFiltersChanged, t, generateMessageId, setAiSearchParams, exampleCity]);

  // Clear this conversation - deletes current conversation from database and clears local state
  const clearCurrentConversation = useCallback(async () => {
    // Delete the current conversation from database if user is authenticated and has a conversation
    if (isAuthenticated && user?.jwt && conversationId) {
      try {
        await deleteConversation({
          jwt: user.jwt,
          conversationId,
        });
      } catch (error) {
        console.error('Error deleting conversation from database:', error);
        // Continue with local state clearing even if database operation fails
      }
    }

    // Clear local state
    clearLocalChatState();
  }, [isAuthenticated, user?.jwt, conversationId, clearLocalChatState]);

  // Clear both local state and database context (all conversations)
  const clearAllChatContext = useCallback(async () => {
    // Delete all conversations from database if user is authenticated
    if (isAuthenticated && user?.jwt) {
      try {
        await deleteAllConversations({
          jwt: user.jwt,
        });
        // Clear the current conversation ID
        setConversationId(null);
      } catch (error) {
        console.error('Error deleting conversations from database:', error);
        // Continue with local state clearing even if database operation fails
      }
    }

    // Clear local state as well
    clearLocalChatState();
  }, [isAuthenticated, user?.jwt, clearLocalChatState]);

  const handleClearChat = useCallback(() => {
    if (isAuthenticated && user?.jwt) {
      openModal(CLEAR_CHAT_MODAL, { 
        onClearLocal: clearCurrentConversation,
        onClearAll: clearAllChatContext,
      } as ClearChatOptions);
    } else {
      clearLocalChatState();
    }
  }, [openModal, clearCurrentConversation, clearAllChatContext, isAuthenticated, user?.jwt, clearLocalChatState]);

  // Expose clearChat handler to parent via ref
  useEffect(() => {
    if (clearRef) {
      clearRef.current = handleClearChat;
    }
    return () => {
      if (clearRef) {
        clearRef.current = null;
      }
    };
  }, [clearRef, handleClearChat]);

  // Expose a send-message handler to parent via ref.
  // Appends `text` to the current input value (comma-separated when the input
  // already has content) and focuses the field. If appending would exceed the
  // 250-char limit the current value is preserved unchanged.
  useEffect(() => {
    if (sendMessageRef) {
      sendMessageRef.current = (text: string) => {
        setInputValue(prev => {
          const trimmed = prev.trim();
          const combined = trimmed.length === 0 ? text : `${trimmed}, ${text}`;
          return combined.length <= 250 ? combined : prev;
        });
        inputRef.current?.focus();
      };
    }
    return () => {
      if (sendMessageRef) {
        sendMessageRef.current = null;
      }
    };
  }, [sendMessageRef]);

  const handleInfoIconClick = useCallback(() => {
    setShowDisclaimerTooltip(prev => !prev);
  }, []);

  const handleCloseDisclaimer = useCallback(() => {
    setShowDisclaimerTooltip(false);
  }, []);

  // close disclaimer panel when clicking outside
  const outsideClickRef = useOutsideClickHandler<HTMLDivElement>(handleCloseDisclaimer);

  return (
    <div className={`${styles['chat-bot']} ${className || ''}`}>
      {!hideHeader && (
        <div className={styles['chat-header']}>
          <div className={styles['title-container']}>
            <h3>AI Search</h3>
            <PillBadge 
              backgroundColor="#1B489B"
              textColor="#FFFFFF"
              size="small"
              className={styles['beta-tag']}
            >
              BETA
            </PillBadge>
            <InfoIcon 
              size={16} 
              onClick={handleInfoIconClick}
              className={styles['info-icon']}
            />
          </div>
          <Button
            label="Clear"
            theme={SECONDARY_THEME}
            onClick={handleClearChat}
            className={styles['clear-button']}
          />
        </div>
      )}
      
      {showDisclaimerTooltip && (
        <div ref={outsideClickRef} className={styles['disclaimer-panel']}>
          <button 
            className={styles['close-disclaimer']}
            onClick={handleCloseDisclaimer}
            aria-label="Close disclaimer"
          >
            <CloseIcon />
          </button>
          <p>{disclaimerText}</p>
        </div>
      )}
      
      <div className={styles['chat-messages']}>
        {messages.map(message => {
        // Populate suggestions from the message's metadata
          let suggestions: string[] = [];
          if (message.metadata?.suggestions) {
            try {
              suggestions = JSON.parse(message.metadata.suggestions);
            } catch (e) {
              console.error('Error when parsing suggestions:', e);
            }
          }

          return (
            <div
              key={message.id}
              className={`${styles['message']} ${styles[`message-${message.role}`]} ${message.isWelcomeMessage ? styles['welcome-message'] : ''}`}
            >
              <div className={styles['message-content']}>
                {message.content}
              </div>
              {suggestions.length > 0 && (
                <div className={styles['message-suggestions']}>
                  💡 {formatSuggestions(suggestions)}
                </div>
              )}
              {message.role === 'assistant' && message.savedMessageId && (
                <div className={styles['message-vote-buttons']}>
                  <MessageVoteButtons 
                    messageId={message.savedMessageId} 
                    conversationId={conversationId} 
                    initialVote={getVoteFromMetadata(message.metadata)}
                    initialMetadata={message.metadata}
                  />
                </div>
              )}
            </div>
          );
        })}
        {isLoading && (
          <div className={`${styles['message']} ${styles['message-assistant']}`}>
            <div className={styles['message-content']}>
              <div className={styles['typing-indicator']}>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      {renderBeforeInput}
      <div className={buildClassName(styles['input-length-counter'], inputValue.length >= 250 && styles['error'])}>
        {inputValue.length}/250
      </div>
      <div className={styles['chat-input']}>
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={e => {
            const newValue = e.target.value;
            if (newValue.length <= 250) {
              setInputValue(newValue);
            }
          }}
          onKeyDown={handleKeyPress}
          placeholder={t(LOCALE_COMMON_PATHS.SMART_CHAT_PLACEHOLDER, { defaultValue: '2-bed condo under $1M' })}
          disabled={isLoading}
          className={styles['input-field']}
        />
        <Button
          Icon={PaperAirplaneIcon}
          theme={PRIMARY_THEME}
          onClick={handleSendMessage}
          disabled={!inputValue.trim() || isLoading}
          className={styles['send-button']}
          testId="chatbot-send-button"
        />
      </div>
    </div>
  );
};

export default ChatBot;
 